import * as React from 'react';
import RolesListComponent, { RolesListFooter } from '../RolesListComponent/RolesListComponent';
import { useSelector, useDispatch } from 'react-redux';
import { RouteChildrenProps, useParams, withRouter } from 'react-router';
import Breadcrumbs from '../Breadcrumbs/Breadcrumbs';
import { translate, InjectedTranslateProps } from 'react-i18next';
import { isUserRowData, TeamMemberTableRowData, isTeamRowData } from '../../common/types';
import { renderNameCell } from '../../common/TeamMembersList/TeamMembersListComponents';
import { UserImage } from '../../common/UserImage/UserImage';
import { setTeamMemberRolesV2 } from '../../redux/actions/serverApi.actions';
import { useCallback, useState, useEffect, useMemo } from 'react';
import { withExperiments, InjectedExperimentsProps } from '@wix/wix-experiments-react';
import { useHistory, useLocation } from 'react-router-dom';
import { State } from '../../redux/configure-store';
import PageActionBar from '../PageActionBar/PageActionBar';
import * as s from './EditPermissionsPage.scss';
import { reportUpdateRoles } from '../../redux/actions/bi.actions';
import { TeamContributorImage } from '../../common/TeamContributorImage/TeamContributorImage';
import { getRowFromUser, getRowFromInvite, getRowFromTeam } from '../../services/team-table-data-convertor';
import { getRoles } from '../../redux/selectors/roleAreas';
import { showModal } from '../../redux/actions/activeModal.actions';
import { ModalName } from '../../types/ModalNames';

import { Page, Card, Box } from 'wix-style-react';

const getUserOrInviteDataById = (teamMembers, id: string): TeamMemberTableRowData => {
  const user = teamMembers.users.find((userEntry) => userEntry.id === id) || {};
  const invite = teamMembers.invites.find((inviteEntry) => inviteEntry.id === id) || {};
  const team = teamMembers.teams.find((teamEntry) => teamEntry.id === id) || {};
  const contributor = user.id ? getRowFromUser(user) : (invite.id ? getRowFromInvite(invite) : getRowFromTeam(team));
  return contributor;
};

interface IEditPermissionsPage extends RouteChildrenProps, InjectedTranslateProps, InjectedExperimentsProps {
}

const EditPermissionsPage: React.FC<IEditPermissionsPage> = ({ t, match, experiments }) => {

  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation<any>();

  const { id } = useParams();

  const { contributorData, permissions, locale } = useSelector((state: State) => (
    {
      contributorData: getUserOrInviteDataById(state.teamMembers, id),
      permissions : state.teamDetails.permissions,
      locale: state.locale
    }));

  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);

  const [isPageDirty, setIsPageDirty] = useState<boolean>(false);
  const [shouldDisplayError, setShouldDisplayError] = useState<boolean>(false);

  const roles = useSelector(getRoles);

  useEffect(() => {
    if (location.state?.roleIds) {
      setSelectedRoles(location.state.roleIds);
    } else if (!isPageDirty) {
      setSelectedRoles(contributorData.roles?.filter(role => roles.some(_role => _role.id === role)) || []);
    }
    }, [contributorData.roles, roles, location]);

  const saveRole = useCallback(() => {
    if (selectedRoles.length === 0) {
      setShouldDisplayError(true);
    } else {
      dispatch(reportUpdateRoles(id, selectedRoles.map(selectedRole => selectedRole)));
      dispatch(setTeamMemberRolesV2(contributorData,  selectedRoles));
      history.push('/', { origin: '/edit' });
    }
  }, [id, contributorData, selectedRoles]);

  const updateSelectedRoles = useCallback((newSelectedRoles) => {
    if (shouldDisplayError && newSelectedRoles.length > 0) {
      setShouldDisplayError(false);
    }
    setSelectedRoles(newSelectedRoles);
    if (!isPageDirty) {
      setIsPageDirty(true);
    }
  }, [isPageDirty, shouldDisplayError]);

  const actionBar = <PageActionBar onOk={saveRole} isEditable={true} isPageDirty={isPageDirty} origin="/edit"/>;
  const onBackAction = useCallback(() => {
    if (isPageDirty) {
      dispatch(showModal(ModalName.DiscardChanges, { onOk: () => history.push('/') }));
    } else {
      history.push('/');
    }
  }, [history, isPageDirty]);
  const experimentCustomRoles = useMemo(() => experiments.enabled('specs.wos.CustomRoles'), [experiments]);

  return (
    <Page height="calc(100vh - 48px)">
      <Page.Header
        showBackButton
        onBackClicked={onBackAction}
        dataHook="edit-permissions-header"
        title={t('user.editPermissions')}
        actionsBar={actionBar}
        breadcrumbs={
          <Breadcrumbs routePath={match.path} disableChangePath={isPageDirty}/>
        }
      />
      <Page.Content>
        <Card className={s.cardContent}>
          <Card.Header
            title={<Box>
              {isTeamRowData(contributorData) ?
                <TeamContributorImage rowData={contributorData}/> :
                <UserImage profileImage={isUserRowData(contributorData) ? contributorData.profileImage : ''} rowData={contributorData}/>
              }
              <Box verticalAlign="middle" marginLeft="medium">
                { renderNameCell(permissions, locale, { showIcon: true, fontLargeMode: true })(contributorData) }
              </Box>
            </Box>}/>
          <Card.Divider />
          <Card.Content>
            <RolesListComponent selectedRoles={selectedRoles} updateSelectedRoles={updateSelectedRoles} shouldDisplayError={shouldDisplayError} origin={`/permissions/edit/${id}`} roleIds={selectedRoles}/>
          </Card.Content>
          {experimentCustomRoles && <RolesListFooter t={t} origin={`/permissions/edit/${id}`} roleIds={selectedRoles}/>}
        </Card>
        <Box paddingTop="large" align="right">
          {actionBar}
        </Box>
      </Page.Content>
    </Page>
  );
};

export default withRouter(withExperiments(translate()(EditPermissionsPage)));
